import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, IconButton, Radio, Chip,
  CircularProgress, Alert,
} from '@mui/material'
import { Close, Layers, ArrowForward } from '@mui/icons-material'
import { modalRows, usedByLabel, canSwitch, modalIntro } from './basePlanSwitch'

const GREEN = '#15803D'
const SOFT_GREEN = '#F0FDF4'
const COLS = '36px 2.2fr 1.8fr 1.6fr 0.7fr 0.9fr 1.1fr'

// FAR-79: pick the Base Plan this Production Plan is built on. Switching
// re-points only this plan; Base Plans never change, and the plan's
// modifications are re-applied on top of the new one.
//
// The current Base Plan is selected on open, and Switch stays disabled until
// another is picked. Cancel / × / Escape / clicking outside discard the pick.
export default function BasePlanModal({
  open, onClose, basePlans, currentId, planName, modificationName, onSwitch, readOnly, error,
}) {
  const rows = useMemo(() => modalRows(basePlans, currentId), [basePlans, currentId])
  const [selected, setSelected] = useState(currentId)
  const [busy, setBusy] = useState(false)
  // A ref, not just state: two clicks in the same tick both see busy=false,
  // and the switch was sent twice (found on dev).
  const inFlight = useRef(false)

  // Re-open fresh: the current one selected, nothing busy.
  useEffect(() => {
    if (open) {
      setSelected(currentId)
      setBusy(false)
      inFlight.current = false
    }
  }, [open, currentId])

  const close = () => { if (!busy) onClose() }
  const doSwitch = async () => {
    if (inFlight.current || !canSwitch(selected, currentId, busy)) return // ignore repeat clicks
    inFlight.current = true
    setBusy(true)
    try {
      await onSwitch(selected)
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={close} maxWidth={false}
      PaperProps={{ sx: { width: 1040, maxWidth: 'calc(100vw - 32px)' } }} data-testid="base-plan-modal">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Layers sx={{ color: GREEN }} aria-hidden />
        <Box sx={{ flex: 1 }}>Base Plan</Box>
        <IconButton aria-label="Close" onClick={close} disabled={busy}><Close /></IconButton>
      </DialogTitle>
      <DialogContent>
        {/* §3.1, worded as the spec. */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} data-testid="base-plan-intro">
          {modalIntro(planName, modificationName)}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {readOnly && (
          <Alert severity="info" sx={{ mb: 1.5 }}>This plan is approved, so its Base Plan can't be changed.</Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Available Base Plans · {rows.length}</Typography>
          <Typography variant="caption" color="text.secondary">Most recent first · shared across Production Plans</Typography>
        </Box>

        <Box role="radiogroup" aria-label="Base Plans" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
          <Box sx={{
            display: 'grid', gridTemplateColumns: COLS, px: 1, py: 0.75, bgcolor: 'grey.50',
            borderBottom: '1px solid', borderColor: 'divider', fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary',
          }}>
            <span />
            <span>Base Plan</span><span>Demand data used</span><span>Horizon</span>
            <Box sx={{ textAlign: 'right' }}>SKUs</Box><Box sx={{ textAlign: 'right' }}>Units</Box>
            <Box sx={{ textAlign: 'right' }}>Used by</Box>
          </Box>
          <Box sx={{ maxHeight: 6 * 52, overflowY: 'auto' }}>
            {rows.length === 0 && (
              <Typography variant="body2" color="text.disabled" sx={{ p: 2, fontStyle: 'italic' }}>No Base Plans exist for this plant.</Typography>
            )}
            {rows.map((r) => {
              const isSel = r.id === selected
              const pick = () => { if (!busy && !readOnly) setSelected(r.id) }
              return (
                <Box key={r.id} role="radio" aria-checked={isSel} tabIndex={readOnly ? -1 : 0}
                  data-testid={`bp-row-${r.id}`} onClick={pick}
                  onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); pick() } }}
                  sx={{
                    display: 'grid', gridTemplateColumns: COLS, alignItems: 'center', px: 1, minHeight: 52,
                    cursor: readOnly ? 'default' : 'pointer', borderBottom: '1px solid', borderColor: 'divider',
                    borderLeft: `4px solid ${isSel ? GREEN : 'transparent'}`,
                    bgcolor: isSel ? SOFT_GREEN : 'transparent',
                    '&:hover': readOnly ? undefined : { bgcolor: isSel ? SOFT_GREEN : 'action.hover' },
                    '&:last-of-type': { borderBottom: 0 },
                  }}>
                  <Radio size="small" checked={isSel} tabIndex={-1} disabled={readOnly}
                    sx={{ p: 0.5, '&.Mui-checked': { color: GREEN } }} inputProps={{ 'aria-hidden': true }} />
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.label}</Typography>
                      {r.isCurrent && <Chip size="small" label="Current" data-testid="stamp-current"
                        sx={{ height: 18, fontSize: '0.65rem', bgcolor: GREEN, color: '#fff' }} />}
                      {r.isNewest && <Chip size="small" label="Newest" data-testid="stamp-newest"
                        sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#DCFCE7', color: GREEN }} />}
                    </Box>
                    <Typography variant="caption" color="text.secondary">{r.by}</Typography>
                  </Box>
                  <Typography variant="body2">{r.demand}</Typography>
                  <Typography variant="body2">{r.horizon}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'right' }}>{r.skus == null ? '—' : r.skus.toLocaleString()}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'right' }}>{r.unitsM == null ? '—' : `${r.unitsM.toFixed(1)}M`}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'right' }}>{usedByLabel(r)}</Typography>
                </Box>
              )
            })}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={close} disabled={busy}>Cancel</Button>
        <Button variant="contained" data-testid="switch-base-plan"
          disabled={readOnly || !canSwitch(selected, currentId, busy)} onClick={doSwitch}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
          endIcon={busy ? null : <ArrowForward />}
          sx={{ bgcolor: GREEN, '&:hover': { bgcolor: '#166534' } }}>
          Switch Base Plan
        </Button>
      </DialogActions>
    </Dialog>
  )
}
